import { useContext, useEffect, useState } from 'react';
import { MdOutlineLocalLaundryService } from 'react-icons/md';
import { FormattedMessage, useIntl } from 'react-intl';

import { TerminalContext, TerminalContextInterface } from '../../TerminalInit';
import { useSlot, useSlots } from '../../api/slots';
import { useSpotLayoutItems } from '../../api/spotLayoutItems';
import { useSpotLayout } from '../../api/spotLayouts';
import { SpotType, useSpot } from '../../api/spots';
import { useMutateCreateTransaction, useTransaction } from '../../api/transactions';
import { Transaction } from '../../common/transactions';
import ActionCard from '../../components/domain/ActionCard';
import { AllowedActionViewProps } from '../../components/domain/AllowedAction';
import useSpotLayoutItemsForSlots from '../../hooks/useSpotLayoutItemForSlot';
import { getRandomString } from '../../utils';
import BackAndHomeNavigationButtons from '../../views/common/BackAndHomeNavigationButtons';
import BaseView from '../../views/common/BaseView';
import InfoView from '../../views/common/InfoView';
import LoadingView from '../../views/common/LoadingView';
import SlotOpenSpotLayout from '../../views/spotlayout/slotOpenSpotLayouts/SlotOpenSpotLayout';
import JustDrop from '../JustDrop';
import { isNikeContactData } from '../nike/api';

type NikeLaundryDropoffActionProps = AllowedActionViewProps;

const NikeLaundryDropoffView = (props: NikeLaundryDropoffActionProps) => {
    const intl = useIntl();
    const { terminal } = useContext<TerminalContextInterface>(TerminalContext);
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url);
    const { data: spotLayoutItems } = useSpotLayoutItems({ spotLayout: spotLayout }, { enabled: !!spotLayout });
    const { data: spot } = useSpot(spotLayout?.spot_url);
    const [error, changeError] = useState<string | null>(null);
    const [transactionId, changeTransactionId] = useState<string | null>(null);
    const [transaction, changeTransaction] = useState<Transaction | null>(null);
    const refetchedTransaction = useTransaction(undefined, transactionId ?? undefined, !!transactionId && !transaction?.slot_id);
    const { data: slot } = useSlot(transaction?.slot_id);
    const { data: laundrySlots } = useSlots({ spot: spot?.id, type: 'dropoff_laundry', status: 'available', transaction_type: 'pudo' }, { enabled: !!spot });
    const [slotsConfirmed, changeSlotsConfirmed] = useState<boolean>(false);
    const slotItems = useSpotLayoutItemsForSlots(laundrySlots && laundrySlots.length > 0 ? [laundrySlots[0]] : []);

    const backHomeNav = (
        <BackAndHomeNavigationButtons
            onHome={props.onHome}
            onBack={props.onBack}
        />
    );

    const mutateCreateTransaction = useMutateCreateTransaction({
        onSuccess: (transaction) => {
            changeTransactionId(transaction.id);
        },
        onError: (error) => {
            changeError(
                intl.formatMessage({
                    id: 'workflow.AuthorisedContactWorkflow.NikeLaundryDropoffAction.noSlotsAvailableError',
                    description: 'Error message when no slots are available for laundry dropoff',
                    defaultMessage: 'No more slots available for laundry dropoff. Please try again later.',
                })
            );
        },
    });

    useEffect(() => {
        if (spot && slotsConfirmed) {
            mutateCreateTransaction.mutate({
                createTransaction: {
                    account_id: spot.account,
                    spot_id: spot.id,
                    tt_number: getRandomString(20),
                    sender_id: props.contact?.id,
                    laundry: true,
                },
            });
        }
    }, [spot, slotsConfirmed]);

    useEffect(() => {
        if (refetchedTransaction && refetchedTransaction.slot_id) {
            changeTransaction(refetchedTransaction);
        }
    }, [refetchedTransaction]);

    if (error)
        return (
            <InfoView
                navbarItems={backHomeNav}
                onInactivity={props.onInactivity}
                title={error}
            />
        );

    if (!laundrySlots || slotItems === undefined) {
        return (
            <LoadingView
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
            />
        );
    }

    if (!slotsConfirmed) {
        if (slotItems.length === 0)
            changeError(
                intl.formatMessage({
                    id: 'workflow.AuthorisedContactWorkflow.NikeLaundryDropoffAction.noSlotsAvailableError',
                    description: 'Error message when no slots are available for laundry dropoff',
                    defaultMessage: 'No more slots available for laundry dropoff. Please try again later.',
                })
            );
        return (
            <BaseView
                onInactivity={props.onInactivity}
                navbarItems={backHomeNav}>
                <div className='text-center'>
                    <button
                        className='primary-button btn-lg'
                        onClick={() => changeSlotsConfirmed(true)}>
                        <FormattedMessage
                            id='views.SlotConfirmationView.confirmButton'
                            description='The button label to confirm using the proposed Slot.'
                            defaultMessage='Confirm and open SLOT'
                        />
                    </button>
                </div>
                <SlotOpenSpotLayout
                    type={SpotType.STANDARD_DISTRISPOT}
                    highlightedItems={slotItems}
                />
            </BaseView>
        );
    }

    if (!transaction || !transaction.slot_id || !slot) {
        return (
            <LoadingView
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
            />
        );
    }

    return (
        <JustDrop
            onInactivity={props.onInactivity}
            onHome={props.onHome}
            onBack={props.onBack}
            transaction={transaction}
            senderData={{
                senderContact: props.contact,
            }}
            receiverData={{}}
            skipConfirm
            slot={slot}
            transactionType={transaction.type}
        />
    );
};

function NikeLaundryDropoffAction(props: NikeLaundryDropoffActionProps) {
    const intl = useIntl();
    if (props.contact === undefined) return undefined;
    if (!props.contact.laundry) return undefined;
    if (!isNikeContactData(props.contact.additional_data)) return undefined;

    const nikeLaundryDropoffAction: ActionCard = {
        actionText: intl.formatMessage({
            id: 'workflow.AuthorisedContactWorkflow.NikeLaundryDropoffAction',
            description: 'This is the Laundry Dropoff action for AuthorisedContactWorkflow',
            defaultMessage: 'Laundry dropoff',
        }),
        highlight: true,
        icon: (
            <MdOutlineLocalLaundryService
                className='dashboard-action-icon'
                size={264}
            />
        ),
        view: <NikeLaundryDropoffView {...props} />,
    };

    return nikeLaundryDropoffAction;
}

export default NikeLaundryDropoffAction;
